import { getDbSupabase } from './supabase';
import type { DemPoReviewRow } from '../types/dem';

export type CustItemBlock = {
  customer_no: string;
  item_no: string;
  reason: string;
};

const CHUNK_SIZE = 80;

/** NAV Cust_Item_Block_List rows for a customer + item codes (PowerBI sales schema). */
export async function fetchCustItemBlocks(
  customerNo: string,
  itemNos: string[],
): Promise<Map<string, string>> {
  const customer = customerNo.trim();
  const items = [...new Set(itemNos.map((c) => c.trim()).filter(Boolean))];
  const out = new Map<string, string>();
  if (!customer || items.length === 0) return out;

  const headers: Record<string, string> = { Accept: 'application/json' };
  try {
    const { data } = await getDbSupabase('dem').auth.getSession();
    const token = data.session?.access_token?.trim();
    if (token) headers.Authorization = `Bearer ${token}`;
  } catch {
    // proxy rejects unauthenticated
  }

  for (let i = 0; i < items.length; i += CHUNK_SIZE) {
    const chunk = items.slice(i, i + CHUNK_SIZE);
    const response = await fetch(
      `/cff-api?resource=cust_item_blocks&customer_no=${encodeURIComponent(customer)}&items=${encodeURIComponent(chunk.join(','))}`,
      { headers },
    );
    const json = (await response.json().catch(() => null)) as
      | { blocks?: CustItemBlock[]; error?: string }
      | null;
    if (!response.ok) continue;
    for (const block of json?.blocks ?? []) {
      const item = String(block.item_no ?? '').trim();
      const reason = String(block.reason ?? '').trim();
      if (item && reason) out.set(item.toUpperCase(), reason);
    }
  }
  return out;
}

export function applyCustItemBlocksToReviewRows(
  rows: DemPoReviewRow[],
  blocks: Map<string, string>,
): DemPoReviewRow[] {
  return rows.map((row) => {
    const code = row.sysCode.trim().toUpperCase();
    return {
      ...row,
      sysCustItemBlockReason: code ? blocks.get(code) ?? '' : '',
    };
  });
}

export async function enrichReviewRowsWithCustItemBlocks(
  customerNo: string,
  rows: DemPoReviewRow[],
): Promise<DemPoReviewRow[]> {
  const codes = rows.map((r) => r.sysCode.trim()).filter(Boolean);
  const blocks = await fetchCustItemBlocks(customerNo, codes);
  return applyCustItemBlocksToReviewRows(rows, blocks);
}

export type CustItemBlockLine = {
  itemCode: string;
  custItemBlockReason?: string;
};

export function applyCustItemBlocksToLines<T extends CustItemBlockLine>(
  lines: T[],
  blocks: Map<string, string>,
): T[] {
  return lines.map((line) => {
    const code = line.itemCode.trim().toUpperCase();
    return {
      ...line,
      custItemBlockReason: code ? blocks.get(code) ?? '' : '',
    };
  });
}

export async function enrichLinesWithCustItemBlocks<T extends CustItemBlockLine>(
  customerNo: string,
  lines: T[],
): Promise<T[]> {
  const codes = lines.map((l) => l.itemCode.trim()).filter(Boolean);
  const blocks = await fetchCustItemBlocks(customerNo, codes);
  return applyCustItemBlocksToLines(lines, blocks);
}
